import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Tag, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

// Shipping cost
const SHIPPING_COST = 450;

// Cities with free shipping
const FREE_SHIPPING_CITIES = ['beograd', 'novi sad', 'novi_sad', 'novisad'];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, totalPrice, clearCart } = useCart();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoCodes, setPromoCodes] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    city: '',
    postal_code: '',
    notes: ''
  });

  // Fetch promo codes
  useEffect(() => {
    fetch('/api/promo.php')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setPromoCodes(data);
        }
      })
      .catch(() => {});
  }, []);

  // Track InitiateCheckout when page loads
  useEffect(() => {
    if (items.length > 0 && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        value: totalPrice,
        currency: 'RSD',
        num_items: items.reduce((sum, item) => sum + item.quantity, 0)
      });
    }
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sr-RS').format(price);
  };

  const getProductName = (product) => {
    return language === 'sr' ? product.name_sr : product.name_en;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Check if city has free shipping
  const hasFreeShipping = () => {
    const city = formData.city.toLowerCase().trim();
    return FREE_SHIPPING_CITIES.some(c => city.includes(c) || c.includes(city));
  };

  // Calculate discount
  const getDiscount = () => {
    if (!appliedPromo) return 0;
    if (appliedPromo.type === 'percent') {
      return Math.round(totalPrice * (appliedPromo.value / 100));
    }
    return appliedPromo.value;
  };

  // Calculate shipping
  const getShipping = () => {
    if (hasFreeShipping()) return 0;
    if (appliedPromo && appliedPromo.free_shipping) return 0;
    return SHIPPING_COST;
  };

  const discount = getDiscount();
  const shipping = 0; // Shipping is not added to total - just displayed as info
  const finalTotal = totalPrice - discount;

  // Apply promo code
  const applyPromoCode = () => {
    setPromoError('');
    const code = promoCode.trim().toUpperCase();
    
    if (!code) {
      setPromoError(language === 'sr' ? 'Unesite promo kod' : 'Enter promo code');
      return;
    }

    const found = promoCodes.find(p => p.code.toUpperCase() === code && p.is_active);
    
    if (found) {
      setAppliedPromo(found);
      toast.success(language === 'sr' ? 'Promo kod primenjen!' : 'Promo code applied!');
    } else {
      setPromoError(language === 'sr' ? 'Nevažeći promo kod' : 'Invalid promo code');
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare order items for API
      const orderItems = items.map(item => ({
        id: item.product.id,
        name: item.product.name_sr || item.product.name_en,
        price: item.product.price,
        quantity: item.quantity
      }));

      // Send order to PHP backend
      const response = await fetch('/api/order.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          items: orderItems,
          promo_code: appliedPromo?.code || null,
          discount: discount,
          shipping: shipping,
          subtotal: totalPrice,
          total: finalTotal
        })
      });

      const result = await response.json();

      if (result.success) {
        // Track Meta Pixel Purchase event
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: finalTotal,
            currency: 'RSD',
            num_items: items.reduce((sum, item) => sum + item.quantity, 0)
          });
        }
        
        setOrderId(result.order_number);
        setSuccess(true);
        clearCart();
      } else {
        toast.error(language === 'sr' ? 'Greška pri slanju porudžbine' : 'Error submitting order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(language === 'sr' ? 'Greška pri slanju porudžbine' : 'Error submitting order');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0 && !success) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center bg-[#0A0A0A]">
        <p className="text-white/40 mb-4">{t('cart_empty')}</p>
        <Link to="/shop" className="text-gold hover:text-gold-light">
          {t('cart_continue')}
        </Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center px-6 bg-[#0A0A0A]" data-testid="checkout-success">
        <div className="text-center max-w-md">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-8" />
          <h1 className="font-heading text-3xl md:text-4xl text-white mb-4">
            {t('checkout_success')}
          </h1>
          <p className="text-white/50 mb-2">
            {t('checkout_success_message')}
          </p>
          <p className="text-gold text-sm mb-10">
            {language === 'sr' ? 'Broj porudžbine' : 'Order number'}: {orderId}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black px-10 py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300"
          >
            {t('nav_home')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#0A0A0A]" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-white/40 hover:text-gold transition-colors mb-12 text-sm tracking-wider uppercase"
        >
          <ArrowLeft size={16} />
          {t('back')}
        </Link>

        <div className="text-center mb-12">
          <div className="decorative-line mx-auto mb-8" />
          <h1 className="font-heading text-3xl md:text-4xl text-white">
            {t('checkout_title')}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('checkout_name')} *
                  </label>
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('checkout_email')} *
                  </label>
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                  {t('checkout_phone')} *
                </label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                  {t('checkout_address')} *
                </label>
                <input
                  type="text"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  required
                  className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                  data-testid="checkout-address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('checkout_city')} *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                    data-testid="checkout-city"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                    {t('checkout_postal')} *
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    required
                    className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors"
                    data-testid="checkout-postal"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                  {t('checkout_notes')}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full bg-surface border border-white/10 focus:border-gold outline-none px-4 py-4 text-white transition-colors resize-none"
                  data-testid="checkout-notes"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold hover:bg-gold-light text-black py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300 disabled:opacity-50"
                data-testid="checkout-submit"
              >
                {loading ? t('loading') : t('checkout_submit')}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="glass p-6 elegant-border sticky top-32">
              <h2 className="font-heading text-xl text-white mb-6">
                {language === 'sr' ? 'Vaša porudžbina' : 'Your order'}
              </h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.product.id} className="flex gap-4">
                    <div className="w-16 h-20 bg-surface flex-shrink-0 flex items-center justify-center p-1">
                      <img
                        src={item.product.image_url}
                        alt={getProductName(item.product)}
                        className="max-h-full w-auto"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white text-sm truncate">
                        {getProductName(item.product)}
                      </h3>
                      <p className="text-white/40 text-sm">
                        {item.quantity} x {formatPrice(item.product.price)} RSD
                      </p>
                    </div>
                    <div className="text-white text-sm">
                      {formatPrice(item.product.price * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Promo Code Section */}
              <div className="border-t border-white/10 pt-4 mb-4">
                <label className="block text-xs text-white/30 uppercase tracking-wider mb-2">
                  <Tag size={14} className="inline mr-1" />
                  {language === 'sr' ? 'Promo kod' : 'Promo code'}
                </label>
                {appliedPromo ? (
                  <div className="flex items-center justify-between bg-green-900/20 border border-green-500/30 px-3 py-2 rounded">
                    <span className="text-green-400 text-sm">
                      {appliedPromo.code} - {appliedPromo.type === 'percent' ? `${appliedPromo.value}%` : `${formatPrice(appliedPromo.value)} RSD`}
                    </span>
                    <button 
                      type="button"
                      onClick={removePromoCode}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      {language === 'sr' ? 'Ukloni' : 'Remove'}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder={language === 'sr' ? 'Unesite kod' : 'Enter code'}
                      className="flex-1 bg-surface border border-white/10 focus:border-gold outline-none px-3 py-2 text-white text-sm"
                      data-testid="promo-code-input"
                    />
                    <button
                      type="button"
                      onClick={applyPromoCode}
                      className="bg-gold hover:bg-gold-light text-black px-4 py-2 text-sm font-medium transition-colors"
                      data-testid="apply-promo-btn"
                    >
                      {language === 'sr' ? 'Primeni' : 'Apply'}
                    </button>
                  </div>
                )}
                {promoError && (
                  <p className="text-red-400 text-xs mt-1">{promoError}</p>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50">{language === 'sr' ? 'Međuzbir' : 'Subtotal'}</span>
                  <span className="text-white">{formatPrice(totalPrice)} RSD</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-green-400">
                      {language === 'sr' ? 'Popust' : 'Discount'}
                      {appliedPromo && ` (${appliedPromo.code})`}
                    </span>
                    <span className="text-green-400">-{formatPrice(discount)} RSD</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/50 flex items-center gap-1">
                    <Truck size={14} />
                    {language === 'sr' ? '+ Dostava' : '+ Shipping'}
                  </span>
                </div>
                
                <p className="text-green-400/70 text-xs">
                  {language === 'sr' 
                    ? '✓ Besplatna dostava za Beograd i Novi Sad' 
                    : '✓ Free shipping for Belgrade and Novi Sad'}
                </p>
                
                <div className="flex justify-between items-center pt-2 border-t border-white/10">
                  <span className="text-white/50">{t('cart_total')}</span>
                  <span className="font-heading text-2xl text-gold">
                    {formatPrice(finalTotal)} <span className="text-sm text-white/50">RSD</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
